<script setup lang="ts">
import Cart from '@/components/Cart.vue'
import CartUpsell from '@/components/CartUpsell.vue'
import Modal from '@/components/Modal.vue'
import type { Cart as TCart } from '@/lib/types/Cart'
import * as CartService from '@/services/cart'
import * as RecommendationService from '@/services/recommendation'
import { ref, watch } from 'vue'
import type { WorkState } from '@/lib/types/WorkState'
import type { Product } from '@/lib/types/Product'
import { addPouchReturnEnvelope } from '@/modules/cartLogic'
import { getUpsellOffer, type UpsellOffer } from '@/services/cartUpsell'
import { track } from '@/services/analytics'
import { shipping } from '@/lib/constants'

const cart = ref<TCart | null>(null)
const isCartPage = window.location.pathname === '/cart'
const open = ref(isCartPage)
const cartWorkState = ref<WorkState>('IDLE')
const cartWorkItem = ref<null | string>(null)
const recommendations = ref<Product[] | null>(null)
const isAddingUpsells = ref(false)

let firstUpdate = true

let mixpanelId: string | undefined
if (window.mixpanel && window.mixpanel.get_distinct_id) {
  mixpanelId = window.mixpanel.get_distinct_id()
}

watch(
  open,
  (newValue) => {
    if (newValue) {
      window.mixpanel.track('cart_view')
    }
  },
  { immediate: true },
)

CartService.subscribe(async (c) => {
  if (firstUpdate) {
    firstUpdate = false
  } else {
    open.value = true
  }
  cart.value = c
  cartWorkState.value = 'IDLE'
  cartWorkItem.value = null

  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.value?.item_count.toString() || '0'
    if (cart.value && cart.value.item_count > 0) {
      el.classList.add('is-visible')
    } else {
      el.classList.remove('is-visible')
    }
  })

  recommendations.value =
    await RecommendationService.loadCartUpsellRecommendations(
      window.__BOWER__.customer?.id.toString(),
      cart.value.items.map((item) => `${item.variant_id}`),
    )

  if (mixpanelId && mixpanelId !== cart.value.attributes.mixpanel_id) {
    // Update the cart with the mixpanel ID
    fetch('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        attributes: {
          mixpanel_id: mixpanelId,
        },
      }),
      headers: { 'content-type': 'application/json' },
    })
  }
})

document.addEventListener('click', (event) => {
  let target = event.target as Element
  while (target.tagName !== 'A') {
    if (!target.parentElement) return
    target = target.parentElement
  }
  if (/\/cart$/.test((target as HTMLAnchorElement).href)) {
    open.value = true
    event.preventDefault()
  }
})

const updateSellingPlan = (key: string, sellingPlanId: string) => {
  // console.log('updateFrequency', { key, value })
  cartWorkState.value = 'IN_PROGRESS'
  cartWorkItem.value = key
  const itemInCart = cart.value?.items.find((item) => item.key === key)

  if (itemInCart) {
    CartService.switchSellingPlan(key, sellingPlanId, itemInCart.quantity)
    window.mixpanel.track('cart_switch_to_subscription', {
      productId: itemInCart.product_id.toString(),
      productVariantId: itemInCart.variant_id.toString(),
    })
  } else {
    alert(
      'Something went wrong. Please try adding the item to your cart again.',
    )
    window.Bugsnag.notify(new Error('could not convert update selling plan'))
  }
}

const updateQuantity = (key: string, value: number) => {
  // console.log('updateQuantity', { key, value })
  cartWorkState.value = 'IN_PROGRESS'
  cartWorkItem.value = key
  CartService.updateQuantity(key, value)
}

const remove = (key: string) => {
  // console.log('remove', { key })
  cartWorkState.value = 'IN_PROGRESS'
  cartWorkItem.value = key
  CartService.remove(key)
}

const addUpsellItem = (cartPayload: CartService.PayloadAddToCart) => {
  cartWorkState.value = 'IN_PROGRESS'
  cartWorkItem.value = `upsell::${cartPayload.id}`
  CartService.add(cartPayload)
  window.mixpanel.track('cart_add_upsell', cartPayload)
}

const subscribe = (key: string, sellingPlanId: string) => {
  // console.log('subscribe', { key, sellingPlanId })
  cartWorkState.value = 'IN_PROGRESS'
  cartWorkItem.value = key
  const itemInCart = cart.value?.items.find((item) => item.key === key)

  if (itemInCart) {
    CartService.switchSellingPlan(key, sellingPlanId, itemInCart.quantity)
    window.mixpanel.track('cart_switch_to_subscription', {
      productId: itemInCart.product_id.toString(),
      productVariantId: itemInCart.variant_id.toString(),
    })
  } else {
    alert(
      'Something went wrong. Please try adding the item to your cart again.',
    )
    window.Bugsnag.notify(
      new Error('could not convert cart item to subscription'),
    )
  }
}

const unsubscribe = (key: string) => {
  // console.log('unsubscribe', { key })
  cartWorkState.value = 'IN_PROGRESS'
  cartWorkItem.value = key
  const itemInCart = cart.value?.items.find((item) => item.key === key)
  if (itemInCart) {
    CartService.switchSellingPlan(key, null, itemInCart.quantity)
  } else {
    alert(
      'Something went wrong. Please try adding the item to your cart again.',
    )
    window.Bugsnag.notify(new Error('could not convert cart item to onetime'))
  }
}

const isCheckingOut = ref(false)
const upsellOffer = ref<UpsellOffer | null>(null)

const onCheckout = async () => {
  if (!cart.value) return
  isCheckingOut.value = true
  try {
    upsellOffer.value = await getUpsellOffer(cart.value)
    if (!upsellOffer.value) {
      checkout()
    } else {
      open.value = false
      isCheckingOut.value = false
      track('cart_upsell_offer_view', upsellOffer.value)
    }
  } catch (e) {
    window.Bugsnag.notify(e as Error)
    checkout()
  }
}

const checkoutWithUpsell = async (
  purchaseData: CartService.PayloadAddToCart,
) => {
  isAddingUpsells.value = true
  track('cart_upsell_offer_add', purchaseData)
  await CartService.add(purchaseData, true, 'system')
  checkout()
}

const checkoutWithUpgrades = async (
  upgradeData: {
    key: string
    quantity: number
    sellingPlanId: string
  }[],
) => {
  isAddingUpsells.value = true
  const preUpsellPrice = cart.value?.total_price || 0
  track('cart_upsell_offer_upgrade', { upgradeData })
  for (const upgrade of upgradeData) {
    await CartService.switchSellingPlan(
      upgrade.key,
      upgrade.sellingPlanId,
      upgrade.quantity,
      true,
      'system',
    )
  }
  const postUpsellPrice = (await CartService.refreshCart(true)).total_price
  if (
    preUpsellPrice >= shipping.free_threshold_pence &&
    postUpsellPrice < shipping.free_threshold_pence
  ) {
    // add a discount code to make sure they still get free delivery
    await fetch('/discount/K48WKJT81011')
  }
  checkout()
}

const checkout = () => {
  if (!cart.value) return
  CartService.checkout()
}

const addReturnEnvelope = (quantity: number) => {
  cartWorkState.value = 'IN_PROGRESS'
  addPouchReturnEnvelope(quantity, 'human')
}

document.addEventListener('cart:open', () => {
  // ensures the cart is up to date (onboarding adds stuff without going via cart service)
  CartService.refreshCart()
  open.value = true
})

const cartDict = window.__STRINGS__.cart
</script>

<template>
  <Teleport to="#cart-embed" :disabled="!isCartPage">
    <transition name="Cart" appear>
      <Cart
        v-if="cart && open"
        :cart="cart"
        @close="open = false"
        :work-state="cartWorkState"
        :work-item="cartWorkItem"
        @change-selling-plan="updateSellingPlan"
        @change-quantity="updateQuantity"
        @remove="remove"
        @subscribe="subscribe"
        @unsubscribe="unsubscribe"
        @checkout="onCheckout"
        @add-upsell="addUpsellItem"
        @update-attributes="CartService.updateAttributes"
        @add-pouch-return-envelope="addReturnEnvelope"
        :dictionary="cartDict"
        :recommendations="recommendations"
        :is-embedded="isCartPage"
        :is-checking-out="isCheckingOut"
      ></Cart>
    </transition>
    <Modal v-if="upsellOffer" @close="upsellOffer = null" :no-close="true">
      <CartUpsell
        :upsell-offer="upsellOffer"
        @add="checkoutWithUpsell"
        @upgrade="checkoutWithUpgrades"
        @skip="checkout"
        :is-busy="isAddingUpsells"
      ></CartUpsell>
    </Modal>
  </Teleport>
</template>
